import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import axiosInstance from "../../utils/axiosInstance";
import { Search, Menu, X, ShoppingBag, User, LogOut } from "lucide-react";

// --- NEW FUTURISTIC PALETTE ---
// Background: Very dark slate/black (Void)
// Accent: Electric Cyan (Holographic interface)
const COLORS = {
  bg: "#020617",         // Rich Black
  glass: "rgba(2, 6, 23, 0.8)", // Semi-transparent black
  accent: "#06b6d4",     // Cyan-500 (Base)
  glow: "#22d3ee",       // Cyan-400 (The Glow)
  text: "#e2e8f0",       // Slate-200 (Off-white, easier on eyes)
};

// --- HELPER COMPONENT: Futuristic Nav Link ---
const NavLink = ({ to, children }) => {
  return (
    <Link to={to} className="relative group px-2 py-1 block">
      {/* Text with slight glow on hover */}
      <span className="relative z-10 text-sm uppercase tracking-[0.2em] font-medium text-slate-300 transition-all duration-300 group-hover:text-[#22d3ee] group-hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]">
        {children}
      </span>
      
      {/* The Laser Underline */}
      <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#22d3ee] transform scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100 shadow-[0_0_15px_#22d3ee]"></span>
      
      {/* Top Laser (Subtle Reflection) */}
      <span className="absolute top-0 right-0 w-full h-[1px] bg-[#22d3ee]/30 transform scale-x-0 origin-right transition-transform duration-500 ease-out group-hover:scale-x-100"></span>
    </Link>
  );
};

// --- HELPER COMPONENT: Mobile Nav Link ---
const MobileNavLink = ({ to, children, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="block pl-4 border-l-2 border-transparent hover:border-[#22d3ee] py-3 text-base font-medium text-slate-300 hover:bg-[#22d3ee]/10 hover:text-[#22d3ee] transition-all duration-300"
  >
    {children}
  </Link>
);

const Navbar = () => {
  const { isAuthenticated, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get("/categories");
        setCategories(response.data.sort((a, b) => a.sequence - b.sequence));
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  const topNavCategories = categories.slice(0, 2);
  const subNavCategories = categories.slice(2);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchQuery("");
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* --- MAIN NAVIGATION --- */}
      <nav
        className={`fixed w-full top-0 z-50 transition-all duration-500 border-b border-white/5
        ${
          scrolled
            ? "bg-[#020617]/90 backdrop-blur-xl shadow-[0_10px_30px_-10px_rgba(6,182,212,0.1)] py-0"
            : "bg-[#020617] py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* 1. Logo Section */}
            <div className="flex-shrink-0 flex items-center group cursor-pointer" onClick={() => navigate('/')}>
              <div className="relative flex items-center gap-1">
                {/* Logo Icon/Shape */}
                <div className="w-8 h-8 border border-[#22d3ee] rotate-45 flex items-center justify-center group-hover:bg-[#22d3ee]/10 transition-all duration-500 group-hover:rotate-180 shadow-[0_0_10px_rgba(34,211,238,0.3)]">
                  <div className="w-4 h-4 bg-[#22d3ee] shadow-[0_0_10px_#22d3ee]"></div>
                </div>

                {/* Logo Text */}
                <div className="ml-3 flex flex-col">
                    <span className="font-sans text-xl font-bold tracking-[0.2em] text-white group-hover:text-[#22d3ee] transition-colors duration-300">
                    WEB<span className="text-[#22d3ee]">THEME</span>
                    </span>
                </div>
                
                {/* Logo Background Bloom */}
                <div className="absolute -inset-4 bg-[#22d3ee]/20 blur-2xl rounded-full opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
              </div>
            </div>

            {/* 2. Desktop Menu */}
            <div className="hidden md:flex space-x-10 items-center">
              <NavLink to="/">Home</NavLink>
              {topNavCategories.map((cat) => (
                <NavLink key={cat.id} to={`/products?category=${cat.id}`}>
                  {cat.name}
                </NavLink>
              ))}
            </div>

            {/* 3. Icons & Functional Area */}
            <div className="flex items-center space-x-6">
              
              {/* Search Bar */}
              <form
                onSubmit={handleSearch}
                className={`hidden md:flex items-center relative transition-all duration-300 ${
                  isSearchFocused ? "w-72" : "w-48"
                }`}
              >
                <div className="relative w-full group">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    placeholder="INITIATE SEARCH..."
                    className="w-full bg-[#0f172a] border border-[#1e293b] text-[#22d3ee] text-xs font-mono tracking-widest rounded-sm px-4 py-2.5 pl-10 focus:outline-none focus:border-[#22d3ee] focus:bg-[#0f172a] focus:shadow-[0_0_15px_rgba(34,211,238,0.2)] transition-all duration-300 placeholder-slate-600"
                  />
                  <Search className={`absolute left-3 top-2.5 w-4 h-4 transition-colors duration-300 ${isSearchFocused ? "text-[#22d3ee]" : "text-slate-500"}`} />
                </div>
              </form>

              {/* Action Icons */}
              <div className="hidden md:flex items-center space-x-4">
                {isAuthenticated && isAdmin ? (
                  <>
                    <Link
                      to="/admin"
                      className="p-2 text-slate-400 hover:text-[#22d3ee] transition-transform hover:scale-110 hover:shadow-[0_0_15px_rgba(34,211,238,0.5)] rounded-full border border-transparent hover:border-[#22d3ee]/30 hover:bg-[#22d3ee]/5"
                      title="Dashboard"
                    >
                      <User className="w-5 h-5" />
                    </Link>
                    <Link
                      to="/logout"
                      className="p-2 text-slate-400 hover:text-red-400 transition-transform hover:scale-110"
                      title="Logout"
                    >
                      <LogOut className="w-5 h-5" />
                    </Link>
                  </>
                ) : (
                  <></>
                  //  <button className="p-2 text-slate-400 hover:text-[#22d3ee] transition-colors relative group">
                  //     <ShoppingBag className="w-5 h-5" />
                  //     <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  //       <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-75"></span>
                  //       <span className="relative inline-flex rounded-full h-3 w-3 bg-[#06b6d4]"></span>
                  //     </span>
                  //  </button>
                  
                )}
              </div>

              {/* Mobile Menu Toggle */}
              <button
                className="md:hidden text-slate-300 hover:text-[#22d3ee] transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* --- MOBILE MENU --- */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isMobileMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="bg-[#020617]/95 backdrop-blur-xl border-t border-white/5">
            <div className="px-4 pt-4 pb-6 space-y-2">
              <form onSubmit={handleSearch} className="mb-6 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="SEARCH DATABASE..."
                  className="w-full bg-white/5 text-[#22d3ee] border border-white/10 text-xs font-mono tracking-widest rounded-sm py-3 px-4 focus:outline-none focus:border-[#22d3ee]"
                />
              </form>

              <MobileNavLink to="/" onClick={() => setIsMobileMenuOpen(false)}>HOME</MobileNavLink>
              
              <div className="px-4 py-2 text-[10px] uppercase text-slate-500 tracking-[0.2em] font-bold border-b border-white/5 mb-2">Modules</div>
              {categories.map((cat) => (
                <MobileNavLink
                  key={cat.id}
                  to={`/products?category=${cat.id}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {cat.name}
                </MobileNavLink>
              ))}

              {isAuthenticated && isAdmin && (
                <div className="mt-4 pt-4 border-t border-white/5">
                   <MobileNavLink to="/admin" onClick={() => setIsMobileMenuOpen(false)}>Admin Console</MobileNavLink>
                   <MobileNavLink to="/logout" onClick={() => setIsMobileMenuOpen(false)}>Terminate Session</MobileNavLink>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* --- SUB-NAVBAR (Tech Interface Style) --- */}
      {/* Changed from Yellow Block to Dark Panel with Cyan Border */}
      <div className={`hidden md:block relative z-40 transition-all duration-500 ${scrolled ? 'mt-20' : 'mt-24'}`}>
        
        {/* Background Panel */}
        <div className="w-full bg-[#0f172a] border-y border-[#1e293b]">
          <div className="max-w-7xl mx-auto">
            <div className="flex">
                
                {/* Decorative Left Label */}
                <div className="bg-[#1e293b] px-4 py-2 flex items-center">
                    <span className="text-[10px] text-[#22d3ee] font-mono">SYS.NAV.02</span>
                </div>

                {/* Categories Container */}
                <div className="flex-1 flex flex-row flex-wrap items-center gap-x-1 px-6 py-2">
                    {subNavCategories.length > 0 ? (
                    subNavCategories.map((cat) => (
                        <Link
                        key={cat.id}
                        to={`/products?category=${cat.id}`}
                        className="group relative px-4 py-1 overflow-hidden"
                        >
                        {/* Text */}
                        <span className="relative z-10 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400 group-hover:text-[#020617] transition-colors duration-300">
                            {cat.name}
                        </span>
                        
                        {/* Hover Fill Effect (Cyan Scan) */}
                        <span className="absolute inset-0 bg-[#22d3ee] transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out"></span>
                        </Link>
                    ))
                    ) : (
                    <span className="text-slate-600 text-[10px] font-mono animate-pulse">awaiting_data_stream...</span>
                    )}
                </div>

                 {/* Decorative Right Elements */}
                 <div className="flex items-center px-4 space-x-1">
                    <div className="w-1 h-1 bg-[#22d3ee] rounded-full animate-pulse"></div>
                    <div className="w-1 h-1 bg-[#22d3ee]/50 rounded-full"></div>
                    <div className="w-1 h-1 bg-[#22d3ee]/30 rounded-full"></div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;